import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { useCommentsAndLogs } from "../hooks/useCommentsAndLogs";
import { formatDate } from "../utils/formatDate";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";

interface ChecklistCommentsProps {
    /** stage gate id -> comment `parent_id` */
    parentId: string;
    /** checklist (task) id -> comment `object_id` */
    objectId: string;
    /** comment `object_type` */
    objectType?: string;
    /** comment `comment_topic` */
    commentTopic?: string;
    /** render the list + add box; when false the component only fetches the count */
    expanded: boolean;
    isDisabled?: boolean;
    /** reports the total comment count so the parent can show it on the tab */
    onCountChange?: (count: number) => void;
}

export const ChecklistComments: React.FC<ChecklistCommentsProps> = ({
    parentId,
    objectId,
    objectType = "CHECK_LIST",
    commentTopic = "CHECKLIST_COMMENT_ADDED",
    expanded,
    isDisabled = false,
    onCountChange,
}) => {
    const [comment, setComment] = useState<string>("");
    const [page, setPage] = useState<number>(1);

    const {
        comments: fetchedComments,
        commentsPagination,
        isCommentsPending,
        getComments,
        postComment,
        isAddingComment,
    } = useCommentsAndLogs({
        topic: [commentTopic],
        sub_event: [objectType],
        object_id: objectId,
        parent_id: parentId,
        page,
    });

    const { accumulatedData, handleScroll, resetData } = useInfiniteScroll<any>({
        page,
        setPage,
        fetchedData: fetchedComments,
        pagination: commentsPagination,
        isPending: isCommentsPending,
        resetDependencies: [objectId, parentId],
        isDuplicate: (oldItem: any, newItem: any) =>
            (oldItem.id && oldItem.id === newItem.id) ||
            (!oldItem.id &&
                oldItem.created_date === newItem.created_date &&
                oldItem.comment_text === newItem.comment_text),
    });

    // Fetch on mount / page change so the count is available even while collapsed.
    useEffect(() => {
        getComments(page);
    }, [page, objectId, parentId]);

    useEffect(() => {
        if (commentsPagination) {
            onCountChange?.(commentsPagination.total ?? accumulatedData.length);
        }
    }, [commentsPagination, accumulatedData.length]);

    const handleSave = async () => {
        if (!comment.trim()) return;
        await postComment(comment);
        setComment("");
        resetData();
    };

    if (!expanded) return null;

    return (
        <Box display="flex" flexDirection="column" gap={1.5} mt={1.5}>
            <Box
                display="flex"
                flexDirection="column"
                gap={1.5}
                sx={{ maxHeight: 220, overflowY: "auto" }}
                onScroll={handleScroll}
            >
                {isCommentsPending && accumulatedData.length === 0 ? (
                    <Box display="flex" justifyContent="center" p={1}>
                        <CircularProgress size={20} />
                    </Box>
                ) : accumulatedData.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                        No comments yet.
                    </Typography>
                ) : (
                    accumulatedData.map((value: any, index: number) => (
                        <Box
                            key={value.id ?? index}
                            bgcolor="action.hover"
                            width="100%"
                            p={1.5}
                            borderRadius={1}
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                                <Typography variant="caption" color="text.primary" fontWeight={600}>
                                    {value.user_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {formatDate(value.created_date)}
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                {value.comment_text}
                            </Typography>
                        </Box>
                    ))
                )}
                {isCommentsPending && page > 1 && (
                    <Box display="flex" justifyContent="center" p={1}>
                        <CircularProgress size={18} />
                    </Box>
                )}
            </Box>

            {!isDisabled && (
                <Box>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                        placeholder="Add a comment"
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                    />
                    <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
                        <Button
                            size="small"
                            sx={{ color: "text.primary" }}
                            disabled={isAddingComment}
                            onClick={() => setComment("")}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            variant="text"
                            sx={{ color: "primary.main" }}
                            loading={isAddingComment}
                            disabled={isAddingComment || !comment.trim()}
                            onClick={handleSave}
                        >
                            Comment
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};
